/**
 * Where club calibrations live.
 *
 * localStorage is the PRIMARY copy, always written and read synchronously. That
 * is deliberate: calibration has to work with no network, no profile code and no
 * Cloudflare — a range in a garage with the wifi down still needs the club it
 * was set up with. The remote copy sits on top as durability and portability,
 * not as the source of truth.
 *
 * What the remote buys is the two things localStorage can't do. It is bound to
 * one ORIGIN — the hosted build and FUSE Link's http://localhost:9210 are
 * different origins, so a calibration made on one is invisible to the other —
 * and to one DEVICE, evictable whenever browser data is cleared.
 *
 * Reconciliation is last-write-wins on a stored timestamp, in both directions:
 * a newer remote replaces the local copy on startup, and a newer local one is
 * pushed up. Anything else would lose an offline calibration the moment the
 * network came back.
 */

const DEFAULT_PREFIX = 'fuse.swing.club';
const DEFAULT_ENDPOINT = '/api/calibration';
const PROFILE_KEY = 'fuse.profile';
const PROFILE_LENGTH = 10;
const PROFILE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

export type SyncState = 'off' | 'idle' | 'syncing' | 'synced' | 'error';

export type CalibrationStoreOptions = {
  /**
   * Profile code — the identity, since there are no accounts. Omitted or empty
   * means local-only, which is a supported configuration and not a degraded one.
   */
  profile?: string;
  /** Override for the sync endpoint. Absolute URLs work cross-origin. */
  endpoint?: string;
  prefix?: string;
};

/**
 * Read or mint the device's profile code.
 *
 * Order matters: an explicit code from the URL always wins and is remembered, so
 * pasting a headset URL with `?profile=` adopts that profile permanently rather
 * than for one session. Otherwise the remembered one is reused, and only a
 * device that has never had one generates.
 */
export function resolveProfileCode(fromUrl?: string | null): string {
  const clean = (value: string | null | undefined) =>
    (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const explicit = clean(fromUrl);
  if (explicit.length >= 6) {
    try { localStorage.setItem(PROFILE_KEY, explicit); } catch { /* storage off */ }
    return explicit;
  }

  let stored = '';
  try { stored = clean(localStorage.getItem(PROFILE_KEY)); } catch { /* storage off */ }
  if (stored.length >= 6) return stored;

  let generated = '';
  const random = new Uint8Array(PROFILE_LENGTH);
  crypto.getRandomValues(random);
  for (const byte of random) generated += PROFILE_ALPHABET[byte % PROFILE_ALPHABET.length];
  try { localStorage.setItem(PROFILE_KEY, generated); } catch { /* storage off */ }
  return generated;
}

export class CalibrationStore {
  /** Last sync outcome, for status display. */
  state: SyncState;

  #profile: string;
  #endpoint: string;
  #prefix: string;

  constructor(options: CalibrationStoreOptions = {}) {
    this.#profile = (options.profile ?? '').toLowerCase();
    this.#endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
    this.#prefix = options.prefix ?? DEFAULT_PREFIX;
    this.state = this.#profile ? 'idle' : 'off';
  }

  get profile(): string {
    return this.#profile;
  }

  get isRemote(): boolean {
    return !!this.#profile;
  }

  #key(attachment: string): string {
    return `${this.#prefix}.${attachment}`;
  }

  #url(attachment: string): string {
    const separator = this.#endpoint.includes('?') ? '&' : '?';
    return `${this.#endpoint}${separator}profile=${encodeURIComponent(this.#profile)}`
      + `&attachment=${encodeURIComponent(attachment)}`;
  }

  read(attachment: string): string | null {
    try {
      return localStorage.getItem(this.#key(attachment));
    } catch {
      return null; // private mode / storage disabled — calibration is session-only
    }
  }

  /**
   * Write locally, then publish in the background. Never awaited by the caller:
   * the local write is what makes the calibration real, and a failed upload must
   * not make a successful calibration look like it failed.
   */
  write(attachment: string, payload: string) {
    try {
      localStorage.setItem(this.#key(attachment), payload);
    } catch { /* private mode / storage disabled */ }
    this.push(attachment, payload).catch(() => { /* state already records it */ });
  }

  remove(attachment: string) {
    try {
      localStorage.removeItem(this.#key(attachment));
    } catch { /* private mode / storage disabled */ }
  }

  /** Upload a payload. Resolves false when there's no profile or it failed. */
  async push(attachment: string, payload: string): Promise<boolean> {
    if (!this.#profile) return false;
    this.state = 'syncing';
    try {
      const response = await fetch(this.#url(attachment), {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: payload,
      });
      this.state = response.ok ? 'synced' : 'error';
      return response.ok;
    } catch {
      this.state = 'error';
      return false;
    }
  }

  /** Fetch the stored payload, or null if there is none / no profile / offline. */
  async fetchRemote(attachment: string): Promise<string | null> {
    if (!this.#profile) return null;
    this.state = 'syncing';
    try {
      const response = await fetch(this.#url(attachment), { method: 'GET' });
      if (response.status === 404) {
        this.state = 'synced'; // reached the store; it simply has nothing yet
        return null;
      }
      if (!response.ok) {
        this.state = 'error';
        return null;
      }
      const body = await response.json() as { payload?: unknown };
      this.state = 'synced';
      return body.payload === undefined ? null : JSON.stringify(body.payload);
    } catch {
      this.state = 'error';
      return null;
    }
  }
}
